import './ui/styles.css';
import { banter as bundledBanter, courseLessons, grammarNotes } from './content/index.ts';
import { banterNeeds, loadCachedContent, syncContent } from './content/remote.ts';
import { createAccounts } from './lib/accounts.ts';
import { createAttemptStore } from './lib/attemptLog.ts';
import { createChatClient } from './lib/chatApi.ts';
import { createChatStore } from './lib/chatLog.ts';
import { aiEnabled } from './lib/aiSwitch.ts';
import { createCoachSettings } from './lib/coachSettings.ts';
import { createTeacherSettings } from './lib/teacherSettings.ts';
import type { TeacherLang } from './types.ts';
import { createVariants } from './lib/variants.ts';
import { todayISO } from './lib/dates.ts';
import { shouldAskMood } from './lib/mood.ts';
import { createDeviceStore, pairDevice, releaseDevice } from './lib/device.ts';
import { HOME_ROUTE } from './lib/route.ts';
import type { Route } from './lib/route.ts';
import { createRunStore } from './lib/runSnapshot.ts';
import { createProfilesClient } from './lib/profilesApi.ts';
import { createProfileStore } from './lib/profileStore.ts';
import { createExitGate } from './lib/exitGate.ts';
import { createRecordingQueue } from './lib/recordings.ts';
import { loadProgress, saveProgress } from './lib/progress.ts';
import { createAssessorChain, createBrowserAssessor } from './speech/assessors.ts';
import type { AssessorChain } from './speech/assessors.ts';
import { createPlayer } from './speech/audio.ts';
import { createTokenProvider } from './speech/azure.ts';
import { createAzureListener, createBrowserListener } from './speech/listen.ts';
import { createSpeaker } from './speech/speaker.ts';
import { renderEntry, renderLoading, renderLookScreen } from './ui/accountScreens.ts';
import { renderAdmin } from './ui/admin.ts';
import type { AppCtx } from './ui/ctx.ts';
import { renderHome } from './ui/home.ts';
import { langOf } from './ui/i18n.ts';
import { renderMood } from './ui/moodScreen.ts';
import { runCourse, runReview } from './ui/runner.ts';
import type { LessonV2 } from './lib/levels.ts';
import { renderWeek } from './ui/weekScreen.ts';
import { renderRanking } from './ui/rankingScreen.ts';
import { renderWelcome } from './ui/welcome.ts';
import { applyLook } from './ui/theme.ts';

const root = document.getElementById('app');
if (!root) throw new Error('Brak elementu #app');

const devices = createDeviceStore();

let storage: Storage | null = null;
try {
  storage = localStorage;
} catch {
  /* przeglądarka bez dostępu do pamięci — aplikacja działa na treści wbudowanej */
}

// Konta rodziny i profile dzieci (SPEC §15): postęp należy do profilu i jest zapisywany na serwerze; bez serwera (dev, hosting statyczny) działa jak dotąd lokalnie.
const accounts = createAccounts({
  store: createProfileStore(storage),
  devices,
  client: createProfilesClient(() => devices.headers()),
  local: { load: () => loadProgress(), save: (p) => void saveProgress(p) },
  onLook: applyLook,
});

// Treść: ostatnia poprawna z serwera (zapisana w przeglądarce), a gdy jej nie ma — wbudowana. Zob. src/content/remote.ts.
const needs = { banter: banterNeeds(bundledBanter) };
const cached = loadCachedContent(storage, needs);
let contentVersion = cached?.version ?? '';
// Wszystkie nagrania grają z plików wbudowanych w aplikację (public/audio); od patcha 49 serwer nie podmienia żadnego (dawne nagrania „dni” z R2 zniknęły).
const player = createPlayer('./');

// Zanim sonda Azure się skończy, zakładamy samą przeglądarkę (albo samoocenę, gdy jej brak).
const browser = createBrowserAssessor();
let chain: AssessorChain = { assessors: browser ? [browser] : [], primary: browser ? 'browser' : 'self' };
let chainSeq = 0;
let chainDone: Promise<void> = Promise.resolve();
const refreshChain = (): Promise<void> => {
  const seq = ++chainSeq;
  const tokens = createTokenProvider(undefined, './api/token', undefined, () => devices.headers()); // nowy dostawca = świeży token po zmianie klucza
  chainDone = createAssessorChain({ tokens }).then((c) => {
    if (seq === chainSeq) chain = c; // wolniejsza, starsza sonda nie nadpisze nowszej
  });
  return chainDone;
};
void refreshChain();

// Rozmowa z botem: własny dostawca tokenu Azure (rozpoznawanie swobodnej mowy i głos bota), klient /api/chat i dziennik rozmów w tej przeglądarce.
const chatTokens = createTokenProvider(undefined, './api/token', undefined, () => devices.headers());
const azureListener = createAzureListener(chatTokens);
const browserListener = createBrowserListener();
const botSpeaker = createSpeaker({ tokens: chatTokens });

// Język aplikacji (przełącznik EN/PL): napisy na ekranach i linijki bota. Atrybut <html lang> idzie za nim (czytniki ekranu, podpowiedzi pisowni, dzielenie wyrazów).
const teacher = createTeacherSettings(storage);
const setHtmlLang = (lang: TeacherLang): void => {
  document.documentElement.lang = lang;
};
setHtmlLang(teacher.lang());
teacher.onChange(setHtmlLang);

let onHome = false;
// Odświeżenie profilu po tym, jak rodzic go usunął/zarchiwizował/odłączył urządzenie albo cofnął zgodę na nagrania: na ekranie lekcji niczego nie przerywamy (lib/exitGate.ts).
const exitGate = createExitGate({ atHome: () => onHome, refresh: () => accounts.refresh(), leave: () => void showStart() });
let updateReady = false; // nowy service worker przejął stronę po wdrożeniu — odświeżamy przy najbliższym powrocie na ekran główny
// Powitanie (3 warianty + „Zaczynamy!”) pokazujemy raz na sesję: po wyborze profilu (świeży start aplikacji, „Zmień profil”, „Wyloguj”), a potem ekran główny.
// Odświeżenie strony, powroty z lekcji i przerysowania ekranu głównego (np. po synchronizacji) go nie powtarzają — sesja (lib/route.ts) pamięta, że dziecko je widziało.
// Odświeżenie wraca też na ekran, na którym dziecko było (tydzień, „Mój wygląd”, panel rodzica, lekcja na tym samym zadaniu) — `restore` niżej.
let bootRestore = true;
let showSeq = 0;
const ctx: AppCtx = {
  root,
  player,
  chain: () => chain,
  devices,
  pair: async (pin, name) => {
    const r = await pairDevice(pin, name, devices); // przy ponownym parowaniu sam zwalnia też poprzedni klucz (lib/device.ts)
    if (r.ok) await refreshChain();
    return r;
  },
  unpair: async () => {
    const current = devices.get();
    if (current) await releaseDevice(current.key); // najpierw serwer (klucz jeszcze jest), potem lokalnie
    devices.clear();
    if (accounts.mode() === 'family') accounts.signOut(); // bez klucza nie ma kont: wracamy do logowania
    await refreshChain();
  },
  whenChain: () => chainDone,
  attempts: createAttemptStore(storage),
  // nagrania głosu do strojenia oceny (SPEC §23.39): ruszają dopiero, gdy rodzic włączy zgodę dla profilu w „Dla rodzica”; wysyłka w tle
  recordings: createRecordingQueue({ profile: () => accounts.profile(), headers: () => devices.headers(), onRevoked: () => exitGate.request() }),
  teacher,
  // AI (rozmowa, „Zapytaj bota”, reakcje modelu) jest wstrzymane do odwołania — patrz lib/aiSwitch.ts. Bez `chat` zadania działają bez modelu, a po próbie mówi nauczyciel z nagrań.
  // Ekran rozmowy (`renderChat`) nie ma od patcha 48 wejścia z ekranu głównego: dawał rozmowę „z dnia” starego kursu; wróci razem z warstwą AI, na słownictwie kursu v2 (SPEC §23.27.4).
  ...(aiEnabled(storage)
    ? {
        chat: {
          client: createChatClient(() => devices.headers()),
          listener: () => (chain.azure === 'ok' ? azureListener : browserListener),
          speaker: botSpeaker,
          store: createChatStore(storage),
          coach: createCoachSettings(storage),
        },
      }
    : {}),
  variants: createVariants(storage),
  account: accounts,
  getProgress: () => accounts.progress(),
  setProgress: (p) => accounts.setProgress(p),
  today: () => todayISO(),
  banter: cached?.banter ?? bundledBanter,
  at: (route) => accounts.setRoute(route),
  run: createRunStore(storage),
  goHome: () => {
    // Pierwsze wejście po starcie aplikacji: jeśli to odświeżenie w trakcie sesji, wracamy na zapamiętany ekran (bez powitania i bez ekranu głównego pośrodku).
    if (bootRestore && accounts.active() && accounts.welcomed()) {
      bootRestore = false;
      const back = accounts.route();
      if (back.screen !== 'home' && restore(back)) return;
    }
    accounts.setRoute(HOME_ROUTE); // ekran główny jest „domyślny”: to też kasuje zapamiętaną lekcję, tydzień itd. po powrocie z nich
    if (updateReady) {
      updateReady = false;
      location.reload(); // jest nowa wersja aplikacji (zob. rejestracja service workera na dole pliku)
      return;
    }
    if (exitGate.take()) {
      void accounts.refresh().then((ok) => (ok ? ctx.goHome() : void showStart())); // rodzic usunął profil albo wyłączył urządzenie
      return;
    }
    if (!accounts.active()) {
      void showStart();
      return;
    }
    if (!accounts.welcomed()) {
      onHome = false; // na powitaniu synchronizacja nie przerysowuje ekranu
      renderWelcome(ctx, () => {
        accounts.markWelcomed();
        ctx.goHome();
      });
      return;
    }
    onHome = true;
    renderHome(ctx);
  },
  leaveHome: () => {
    onHome = false;
  },
  // Kurs (CEFR, SPEC §21–23): jedyna ścieżka nauki od patcha 48. Lekcje z levels/*.json, bez bota w zadaniach; koło nastroju raz dziennie przed lekcją (patch-32).
  ...(courseLessons.length > 0
    ? {
        course: { lessons: courseLessons, notes: grammarNotes },
        startCourse: (lesson: LessonV2) => {
          onHome = false;
          player.unlock(); // pierwszy gest użytkownika odblokowuje dźwięk na iOS
          ctx.at?.({ screen: 'run', mode: 'lesson', lesson: lesson.id });
          if (shouldAskMood(ctx.getProgress(), ctx.today())) {
            renderMood(ctx, { onDone: () => runCourse(ctx, lesson), onBack: () => ctx.goHome() });
            return;
          }
          runCourse(ctx, lesson);
        },
        // „Powtórka dnia” (patch-47): bez koła nastroju; odświeżenie wraca na to samo zadanie (trasa `run` + `review`)
        startReview: () => {
          onHome = false;
          player.unlock();
          ctx.at?.({ screen: 'run', mode: 'review' });
          runReview(ctx);
        },
      }
    : {}),
};

/** Wraca na ekran zapamiętany przed odświeżeniem strony. false = taki ekran już nie istnieje (np. lekcję usunięto) i dziecko trafia na ekran główny. */
function restore(route: Route): boolean {
  switch (route.screen) {
    case 'week':
      renderWeek(ctx, route.offset);
      return true;
    case 'look':
      if (!ctx.account) return false;
      renderLookScreen(ctx);
      return true;
    case 'ranking':
      if (ctx.account?.mode() !== 'family') return false;
      renderRanking(ctx, route.scope);
      return true;
    case 'admin':
      renderAdmin(ctx);
      return true;
    case 'run': {
      if (route.mode === 'review') {
        if (!ctx.startReview) return false;
        ctx.startReview();
        return true;
      }
      const lesson = ctx.course?.lessons.find((l) => l.id === route.lesson);
      if (!lesson || !ctx.startCourse) return false;
      ctx.startCourse(lesson); // lekcja sama wraca na zadanie, na którym była (lib/runSnapshot.ts); koło nastroju, jeśli jeszcze nie było, pojawi się jak zwykle
      return true;
    }
    case 'home':
      return false;
  }
}

/** Start i powrót bez aktywnego profilu: sprawdza serwer i pokazuje logowanie, „Kto dziś się uczy?”, pierwszy profil albo ekran główny. */
async function showStart(): Promise<void> {
  const seq = ++showSeq;
  onHome = false;
  renderLoading(ctx);
  const entry = await accounts.start(langOf(ctx)); // komunikat dla dziecka (np. „profil usunięty”) w języku aplikacji
  if (seq === showSeq) renderEntry(ctx, entry);
}

ctx.goHome();

// Synchronizacja postępu: dane z drugiego urządzenia odświeżają ekran główny; profil usunięty przez rodzica albo wyłączone urządzenie kończą sesję.
// Na ekranie lekcji niczego nie przerywamy — wyjście dzieje się przy powrocie na ekran główny.
accounts.onSync((e) => {
  if (e.state === 'gone' || e.state === 'unpaired') {
    exitGate.request();
  } else if (e.changed && onHome) renderHome(ctx);
});
let lastKick = 0;
const kickSync = (force = false): void => {
  if (!force && Date.now() - lastKick < 15_000) return;
  lastKick = Date.now();
  void accounts.syncNow();
};
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') kickSync();
});
window.addEventListener('online', () => kickSync(true));

// Nowe linijki bota z serwera: zapisują się na następny start, a jeśli dziecko jest akurat na ekranie głównym — od razu go odświeża.
// Na ekranie lekcji niczego nie podmieniamy (zadania trzymają własne kopie, a odświeżenie przerwałoby ćwiczenie).
async function refreshContent(): Promise<void> {
  const r = await syncContent(contentVersion, storage, undefined, './api/content', needs);
  if (r.kind !== 'updated') return;
  contentVersion = r.content.version;
  ctx.banter = r.content.banter;
  if (onHome) renderHome(ctx);
}
void refreshContent();

if ('serviceWorker' in navigator && location.protocol === 'https:') {
  // Po wdrożeniu nowy service worker przejmuje stronę (controllerchange). Dziecka nie wyrzucamy z lekcji — odświeżamy aplikację dopiero przy najbliższym
  // powrocie na ekran główny (patrz ctx.goHome), żeby nowe obrazki i ekrany były widać od razu, a nie dopiero po „drugim” uruchomieniu.
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) updateReady = true;
  });
  navigator.serviceWorker.register('./sw.js').then((reg) => {
    const check = () => void reg.update().catch(() => {});
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
  }).catch(() => {});
}
